import React, { useCallback, useMemo, useRef, useState } from 'react';
import type { ColumnsType, ColumnType } from 'antd/es/table';

const DEFAULT_STORAGE_PREFIX = 'kfc.table.columnWidths.';

export type ResizableColumnType<T> = ColumnType<T> & {
  key: string;
  /** 设置后内容可换行，单行最大宽度（拖拽过则取拖拽宽度） */
  wrapMaxWidth?: number;
  /** 换行最大宽度与列宽不超过该 key 列的换行宽度 */
  wrapMaxWidthOf?: string;
};

export interface UseResizableColumnsOptions {
  /** 未拖拽过的列不设宽度，按内容自适应；除 wrapMaxWidth 列外不换行 */
  autoFit?: boolean;
}

function loadStoredWidths(storageKey: string): Record<string, number> {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, number>;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function persistWidths(storageKey: string, widths: Record<string, number>) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(widths));
  } catch {
    // ignore quota / private mode
  }
}

interface ResizableTitleProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  width?: number;
  onResizeStart?: (e: React.MouseEvent) => void;
  onResetWidth?: () => void;
}

/** 表头单元格：右侧拖拽改列宽，双击拖拽柄恢复默认 */
export const ResizableTitle: React.FC<ResizableTitleProps> = (props) => {
  const { onResizeStart, onResetWidth, width, children, style, ...rest } = props;
  if (!onResizeStart) {
    return (
      <th {...rest} style={style}>
        {children}
      </th>
    );
  }
  return (
    <th {...rest} style={{ ...style, position: 'relative', overflow: 'visible', whiteSpace: 'nowrap' }}>
      {children}
      <span
        role="separator"
        aria-orientation="vertical"
        onMouseDown={onResizeStart}
        onDoubleClick={(e) => {
          e.stopPropagation();
          onResetWidth?.();
        }}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          right: -3,
          top: 0,
          bottom: 0,
          width: 8,
          cursor: 'col-resize',
          userSelect: 'none',
          zIndex: 1,
        }}
      />
    </th>
  );
};

/**
 * 可拖拽调整宽度的表格列；宽度按 storageKey 持久化到 localStorage。
 */
export function useResizableColumns<T>(
  baseColumns: ResizableColumnType<T>[],
  storageKeySuffix: string,
  options?: UseResizableColumnsOptions
): ColumnsType<T> {
  const storageKey = DEFAULT_STORAGE_PREFIX + storageKeySuffix;
  const autoFit = options?.autoFit === true;
  const [widths, setWidths] = useState<Record<string, number>>(() => loadStoredWidths(storageKey));
  const widthsRef = useRef(widths);
  widthsRef.current = widths;

  const onResizeStart = useCallback(
    (key: string, fallbackWidth: number) => (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const th = (e.currentTarget as HTMLElement).parentElement;
      const measured = th ? th.getBoundingClientRect().width : 0;
      const startX = e.clientX;
      const startW = measured > 0 ? measured : fallbackWidth;
      let pending: number | null = null;
      let frame: number | null = null;

      // updater 可能延迟到 mouseup 之后执行，只能引用本次拖拽的局部值
      const commit = (value: number) => {
        setWidths((prev) => (prev[key] === value ? prev : { ...prev, [key]: value }));
      };
      const onMove = (ev: MouseEvent) => {
        pending = Math.round(Math.max(56, startW + ev.clientX - startX));
        if (frame != null) return;
        frame = window.requestAnimationFrame(() => {
          frame = null;
          if (pending != null) commit(pending);
        });
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        if (frame != null) {
          window.cancelAnimationFrame(frame);
          frame = null;
        }
        if (pending == null) return;
        const finalWidth = pending;
        commit(finalWidth);
        persistWidths(storageKey, { ...widthsRef.current, [key]: finalWidth });
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [storageKey]
  );

  const onResetWidth = useCallback(
    (key: string) => () => {
      if (!(key in widthsRef.current)) return;
      const next = { ...widthsRef.current };
      delete next[key];
      persistWidths(storageKey, next);
      setWidths(next);
    },
    [storageKey]
  );

  return useMemo(() => {
    const wrapWidthOf = (key: string): number | undefined => {
      const target = baseColumns.find((c) => c.key === key);
      if (!target || target.wrapMaxWidth == null) return undefined;
      return widths[key] ?? target.wrapMaxWidth;
    };
    return baseColumns.map((col) => {
        const { wrapMaxWidth, wrapMaxWidthOf, ...rest } = col;
        const key = col.key;
        const cap = wrapMaxWidthOf ? wrapWidthOf(wrapMaxWidthOf) : undefined;
        const stored = widths[key] != null && cap != null ? Math.min(widths[key], cap) : widths[key];
        const fallback = typeof col.width === 'number' ? col.width : 120;
        const width = autoFit ? stored : stored ?? fallback;

        const next: ColumnType<T> = {
          ...rest,
          width,
          onHeaderCell: () =>
            ({
              width,
              onResizeStart: onResizeStart(key, width ?? fallback),
              onResetWidth: onResetWidth(key),
            }) as React.HTMLAttributes<HTMLElement>,
        };

        if (wrapMaxWidth != null) {
          const origRender = col.render;
          const own = stored ?? wrapMaxWidth;
          const maxWidth = cap != null ? Math.min(own, cap) : own;
          next.render = (value, record, index) => {
            const content = origRender ? origRender(value, record, index) : (value as React.ReactNode);
            return (
              <div style={{ maxWidth, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {content as React.ReactNode}
              </div>
            );
          };
        } else if (autoFit) {
          const origOnCell = col.onCell;
          next.onCell = (record, index) => {
            const base = origOnCell ? origOnCell(record, index) : {};
            return { ...base, style: { ...(base?.style || {}), whiteSpace: 'nowrap' } };
          };
        }
        return next;
      });
  }, [baseColumns, widths, autoFit, onResizeStart, onResetWidth]);
}
